import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  IUserObject,
  IEmployeeRequest,
  IEmployeeUpdateRequest,
} from "../../../../../services/api.types";

interface EmployeeFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initial?: IUserObject | null;
  loading: boolean;
  onSubmit: (data: IEmployeeRequest | IEmployeeUpdateRequest) => void;
  onCancel: () => void;
}

type EmployeeType = "Washer" | "Administrator";

interface FormState {
  firstName: string;
  lastName: string;
  country_code: string;
  phone: string;
  userType: EmployeeType;
}

const emptyForm: FormState = {
  firstName: "",
  lastName: "",
  country_code: "+994",
  phone: "",
  userType: "Washer",
};

const normalizeType = (userType?: string): EmployeeType =>
  userType === "Administrator" ? "Administrator" : "Washer";

const EmployeeFormDialog = ({
  open,
  mode,
  initial,
  loading,
  onSubmit,
  onCancel,
}: EmployeeFormDialogProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormState>(emptyForm);

  // Sync the controlled form whenever the dialog opens or the target changes.
  useEffect(() => {
    if (mode === "edit" && initial) {
      setFormData({
        firstName: initial.firstName ?? "",
        lastName: initial.lastName ?? "",
        country_code: initial.country_code ?? "+994",
        phone: initial.phone ?? "",
        userType: normalizeType(initial.userType),
      });
    } else {
      setFormData(emptyForm);
    }
  }, [mode, initial, open]);

  const isEdit = mode === "edit";

  const handleSubmit = () => {
    if (isEdit) {
      const payload: IEmployeeUpdateRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        userType: formData.userType,
      };
      onSubmit(payload);
    } else {
      const payload: IEmployeeRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        country_code: formData.country_code,
        userType: formData.userType,
      };
      onSubmit(payload);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? t("employees.editEmployee") : t("employees.addEmployee")}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t("employees.firstName")}
          value={formData.firstName}
          onChange={(e) =>
            setFormData({ ...formData, firstName: e.target.value })
          }
          margin="dense"
          required
        />
        <TextField
          fullWidth
          label={t("employees.lastName")}
          value={formData.lastName}
          onChange={(e) =>
            setFormData({ ...formData, lastName: e.target.value })
          }
          margin="dense"
          required
        />
        <Box display="flex" gap={1}>
          <TextField
            label={t("employees.countryCode")}
            value={formData.country_code}
            onChange={(e) =>
              setFormData({ ...formData, country_code: e.target.value })
            }
            margin="dense"
            style={{ width: "150px" }}
            disabled={isEdit}
          />
          <TextField
            fullWidth
            label={t("employees.phone")}
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            margin="dense"
            disabled={isEdit}
          />
        </Box>
        <TextField
          fullWidth
          select
          label={t("employees.employeeType")}
          value={formData.userType}
          onChange={(e) =>
            setFormData({
              ...formData,
              userType: e.target.value as EmployeeType,
            })
          }
          margin="dense"
        >
          <MenuItem value="Washer">{t("employees.typeWasher")}</MenuItem>
          <MenuItem value="Administrator">{t("employees.typeAdministrator")}</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.firstName || !formData.lastName}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : isEdit ? (
            t("common.save")
          ) : (
            t("common.add")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeFormDialog;
